import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import take from 'lodash/take';
import takeRight from 'lodash/takeRight';
import { constants as destinationConstants } from '@eb/destination-utils';

import { eventsCardInfoLookup } from '../../SearchApp/utils';
import {
    BUCKETS_NAMES,
    ORGANIZERS_TO_FOLLOW,
    ORGANIZER_ARTICLE_TYPE,
    PRIMARY_BUCKET,
    SECONDARY_BUCKET,
    LIKED_EVENTS,
} from '../../constants/constants';
import { deepKeysToCamel } from '@eb/transformation-utils';
import { ALL_TAB, FOR_YOU } from '../../constants/tabConfig';

const {
    ENTITY_TYPE_MAP,
    EVENT_CARD_TYPE,
    PROFILE_CARD_TYPE,
} = destinationConstants;

const CARD_INFO_LOOKUP_MAP = {
    [EVENT_CARD_TYPE]: eventsCardInfoLookup,
};

const buildFiveToFollowContent = (fiveToFollow) => {
    let content = [];

    if (!isEmpty(fiveToFollow)) {
        content = fiveToFollow.map((article) => ({
            key: article.key,
            name: article.name,
            type: ORGANIZER_ARTICLE_TYPE,
            bucketContent: article,
        }));
    }

    return content;
};

export const getBucketsContent = (
    { entityContext, entities },
    currencyFormat,
    fiveToFollow,
) => {
    // buckets contains all dynamic buckets that are responsible for requesting their own content
    const buckets = [
        {
            key: ORGANIZERS_TO_FOLLOW,
            type: PROFILE_CARD_TYPE,
            bucketContent: {
                name: '',
                description:
                    'Follow the most popular local organizers and get notified when they create events.',
            },
        },
        ...buildFiveToFollowContent(fiveToFollow),
    ];

    // eventsBuckets contains all buckets recieved from the backend
    const eventBuckets = entityContext.map(({ type, ...bucketContent }) => {
        let result = {
            type,
            ...bucketContent,
        };

        if (has(ENTITY_TYPE_MAP, type) && has(CARD_INFO_LOOKUP_MAP, type)) {
            const { name: bucketName, key: bucketKey } = bucketContent;
            const entityKey = ENTITY_TYPE_MAP[type];

            if (!includes(BUCKETS_NAMES, bucketKey)) {
                // eslint-disable-next-line no-console
                console.warn('Missing Affiliate Code', {
                    bucketName,
                    bucketKey,
                });
            }

            if (bucketContent[entityKey]) {
                const bucketsContent = bucketContent[entityKey].map(
                    (id) => entities[id],
                );
                const events = CARD_INFO_LOOKUP_MAP[type]({
                    bucketKey,
                    bucketsContent,
                    currencyFormat,
                });

                result = {
                    ...result,
                    events,
                };
            }
        }

        return result;
    });

    return flatten([buckets, eventBuckets]);
};

export const getFlatBucketContent = (
    flatBucket,
    currencyFormat,
    featureFlags = {},
    tabKey,
) => {
    const events = deepKeysToCamel(flatBucket.results);
    const shouldShowOnboardingEntryPoint =
        tabKey === ALL_TAB && featureFlags.shouldShowOnboardingEntryPoint;
    const firstBucketEventsNumber = shouldShowOnboardingEntryPoint ? 8 : 9;
    const primaryBucketKey = tabKey === FOR_YOU ? LIKED_EVENTS : PRIMARY_BUCKET;

    let eventBuckets = [];

    if (!isEmpty(events)) {
        const primaryBucketEvents = take(events, firstBucketEventsNumber);

        const primaryBucketParsedEvents = eventsCardInfoLookup({
            bucketsContent: primaryBucketEvents,
            currencyFormat,
            bucketKey: primaryBucketKey,
        });

        eventBuckets = [
            {
                events: primaryBucketParsedEvents,
                key: primaryBucketKey,
                name: 'bucketName',
                template: 'card_small_8',
                type: 'event',
            },
        ];

        if (events.length >= firstBucketEventsNumber && tabKey !== FOR_YOU) {
            /**
             * As we are showing an extra card on the first bucket, we will also have the last card
             * on the second bucket.
             * Using CSS and breakpoints we will show one and hide the other one to match the grid.
             * As we get one more event from the left on the second bucket, we also have to get one
             * more event to the right.
             */
            const secondaryBucketStartingPoint =
                events.length - firstBucketEventsNumber + 1;
            const secondaryBucketEvents = take(
                takeRight(events, secondaryBucketStartingPoint),
                17,
            );
            // if this number is negative, returns []

            const secondaryBucketParsedEvents = eventsCardInfoLookup({
                bucketsContent: secondaryBucketEvents,
                currencyFormat,
                bucketKey: SECONDARY_BUCKET,
            });

            eventBuckets.push({
                events: secondaryBucketParsedEvents,
                key: SECONDARY_BUCKET,
                name: 'bucketName',
                template: 'card_small_8',
                type: 'event',
            });
        }
    }

    return eventBuckets;
};
